import {
  Link,
  Outlet,
  createRootRoute,
  useNavigate,
  useRouterState,
  type AnyContext,
} from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties } from 'react';

import { useAuth } from '../auth/AuthProvider';
import { ChatHostProvider } from '../components/chat/ChatHostProvider';
import { ChatLauncher } from '../components/chat/ChatLauncher';
import { HitlBell } from '../components/chat/incidents/HitlBell';
import { AppSwitcher } from '../components/common/AppSwitcher';
import { ConsoleNavRail } from '../components/common/app-shell';
import { PageActionsContext, PageIdentityContext } from '../components/common/page-identity';
import { useOrgRole } from '../hooks/useOrgRole';
import { routes } from '../lib/constants';

import '../components/chat/incidents/incidents.css';

export const Route = createRootRoute({
  component: RootShell,
});

// Three layout modes for the main column (all full-width — the old
// centered max-width cap was dropped in the 2026-07 nav review so every
// page matches the triage board's width):
//
//   constrained (default) — full-width with the usual gutters; `<main>`
//                           scrolls the page.
//   wide                  — same width/gutters but height-bounded flex
//                           (e.g. the triage board kanban scrolls per
//                           column instead of scrolling the page).
//   full-bleed            — no padding; the route renders its own layout
//                           flush to the viewport edge (the workflow
//                           editor's canvas + right panel).
//
// Match each by a list of regex patterns so adding new routes is one line.
// Only the Inbox's Triage Board view (kanban) needs the wide,
// height-bounded layout — its sibling Inbox views are long-form content
// and need the constrained layout's internal scroll. Anchor with `\/?$`
// so deeper paths fall through to constrained.
// The persistent-agent thread *watch* page is a full-height chat: the timeline
// scrolls internally and the composer stays pinned, so it needs the
// height-bounded layout (not constrained's page-level scroll).
const wideMainPatterns: RegExp[] = [
  /^\/inbox\/board\/?$/,
  // The chat-first landing (DEV-16971): a left thread rail + a conversation
  // whose timeline scrolls internally with a pinned composer — same height-
  // bounded shape as the persistent-agent thread-watch page. Covers both
  // `/chat` (greeting) and `/chat/<threadId>`.
  /^\/chat(\/[^/]+)?\/?$/,
  // The interaction map owns the remaining height below its tabs. Keeping it
  // out of constrained mode avoids a tiny page scrollbar from viewport-height
  // arithmetic plus the shell's padding and borders.
  /^\/agents\/orchestration\/?$/,
  /^\/agents\/[^/]+\/threads\/[^/]+\/?$/,
  // Named-bot threads reuse the same full-height watch (composer pinned,
  // transcript scrolls). `/bot/:id` is not under `/agents/.../threads`.
  /^\/bot\/[^/]+\/?$/,
  // Dashboard renderer: a non-scrolling widget canvas that owns the
  // remaining height (kind=grid never scrolls; kind=single is full-bleed).
  /^\/dashboards\/[^/]+\/?$/,
  // Spec detail's node-graph view (xyflow) needs a real pixel height to lay
  // itself out in — constrained mode's page-level scroll never gives it
  // one. The specs *list* page (no trailing id) stays constrained, it's an
  // ordinary scrolling table.
  /^\/agents\/orchestration\/specs\/[^/]+\/?$/,
];

export function isWideMainPath(pathname: string): boolean {
  return wideMainPatterns.some((re) => re.test(pathname));
}

// The chat-first landing IS the full-page chat, so the floating chat launcher
// is redundant there and hidden (DEV-16971). It stays mounted on every other
// page. Matches `/chat` and any `/chat/<threadId>`.
export function isChatSurface(pathname: string): boolean {
  return pathname === routes.CHAT || pathname.startsWith(`${routes.CHAT}/`);
}
// `/workflows/<id>` is the full-bleed workflow editor (canvas). The `(?!agents$)`
// guard keeps the Agents list at `/workflows/agents` out of full-bleed so it
// renders like every other list page. The Inbox's Coordinator view is the
// cinematic live stage — it letterboxes itself into whatever height it
// gets, so it needs the full-bleed cell.
const fullBleedPatterns: RegExp[] = [
  /^\/workflows\/(?!agents$)[^/]+$/,
  /^\/inbox\/coordinator\/?$/,
];

function pickMainStyle(pathname: string): CSSProperties {
  if (fullBleedPatterns.some((re) => re.test(pathname))) return fullBleedMain;
  // The chat surface renders its own flush 3-column shell (thread rail |
  // conversation | incidents rail), so it wants wideMain's height-bounded
  // flex column but with NO padding — otherwise the shell's --sp-6/--sp-7
  // gutter leaves dead black space above/below and beside the rails.
  if (isChatSurface(pathname)) return chatMain;
  if (isWideMainPath(pathname)) return wideMain;
  return constrainedMain;
}

// Scroll container for constrained pages. `<main>` fills the shell body's
// remaining width and scrolls internally so the top bar and nav rail —
// siblings of `<main>` — stay frozen in place. The padding lives on
// `constrainedInner` so the scrollbar sits at the cell's right edge
// (flush with the viewport) rather than inside the gutter.
const constrainedMain: CSSProperties = {
  overflow: 'auto',
  minWidth: 0,
  minHeight: 0,
};

const constrainedInner: CSSProperties = {
  padding: 'var(--sp-6) var(--sp-7)',
  width: '100%',
  minWidth: 0,
};

const wideMain: CSSProperties = {
  // Height-bounded flex column: the page chrome (top bar + nav rail +
  // filter bar) stays fixed and the Kanban below the filter bar takes
  // the remaining height. Its per-column bodies handle the scroll so
  // each column scrolls independently. `min-height: 0` lets this cell
  // shrink inside the flex row instead of pushing the page taller.
  padding: 'var(--sp-6) var(--sp-7)',
  maxWidth: 'none',
  width: '100%',
  margin: '0 auto',
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

// Chat surface: full-bleed like fullBleedMain but keeps wideMain's
// `overflow: hidden` so the internally-scrolling conversation + rails stay
// height-bounded and the page itself never scrolls.
const chatMain: CSSProperties = {
  padding: 0,
  width: '100%',
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const fullBleedMain: CSSProperties = {
  // No padding / max-width — the route renders its own layout flush to
  // the right edge. `min-width: 0` + `min-height: 0` are needed so the
  // flex cell can shrink below its content's intrinsic size — without
  // `min-height: 0` the column-flex children with long content (e.g.
  // an inline run-detail with a multi-line error trace in the right
  // panel) push `main` past the viewport and the page scrolls instead
  // of the panel.
  padding: 0,
  width: '100%',
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const railCollapsedKey = 'console.navRailCollapsed';

/**
 * The nav rail's displayed collapsed state (DEV-16971). On the chat surface the
 * rail auto-collapses to its icon-only strip so the chat thread rail is the
 * primary sidebar — but the platform pages stay one click away as icons, and
 * the user can still expand the rail for the current visit (`chatRailExpanded`)
 * without disturbing their persisted global preference (`railCollapsed`).
 * Everywhere else the persisted preference wins. Exported for unit tests.
 */
export function resolveRailCollapsed(args: {
  onChat: boolean;
  chatRailExpanded: boolean;
  railCollapsed: boolean;
}): boolean {
  const { onChat, chatRailExpanded, railCollapsed } = args;
  return onChat ? !chatRailExpanded : railCollapsed;
}

function RootShell() {
  const pathname = useRouterState({
    select: (s: { location: { pathname: string } } & AnyContext) => s.location.pathname,
  });
  const navigate = useNavigate();
  const { ready, token } = useAuth();
  const isFde = useOrgRole() === 'fde';
  const mainStyle = pickMainStyle(pathname);
  const [railCollapsed, setRailCollapsed] = useState(
    () => localStorage.getItem(railCollapsedKey) === '1',
  );
  const onChat = isChatSurface(pathname);
  useEffect(() => {
    if (!ready || !token) return;
    if (!isFde && !onChat) navigate({ to: '/chat' }).catch(() => undefined);
  }, [ready, token, isFde, onChat, navigate]);
  // Per-visit override for the auto-collapse on the chat surface. Resets each
  // time we leave chat so the rail re-collapses on the next visit.
  const [chatRailExpanded, setChatRailExpanded] = useState(false);
  useEffect(() => {
    if (!onChat) setChatRailExpanded(false);
  }, [onChat]);
  const railCollapsedEffective = resolveRailCollapsed({ onChat, chatRailExpanded, railCollapsed });
  // Topbar mount nodes for the page's chrome — PageHeader portals the
  // active page's title into Z2 and its action buttons into Z3a.
  // Callback-ref state (not plain refs) so pages re-render once the
  // nodes exist on first paint.
  const [identityNode, setIdentityNode] = useState<HTMLElement | null>(null);
  const [actionsNode, setActionsNode] = useState<HTMLElement | null>(null);
  const toggleRail = () => {
    // On chat, the hamburger flips the per-visit override; the persisted global
    // preference is left untouched. Elsewhere it sets the persisted preference.
    if (onChat) {
      setChatRailExpanded((v) => !v);
      return;
    }
    setRailCollapsed((v) => {
      localStorage.setItem(railCollapsedKey, v ? '0' : '1');
      return !v;
    });
  };
  const isFullBleed = fullBleedPatterns.some((re) => re.test(pathname));
  const isWide = isWideMainPath(pathname);
  const isConstrained = !isFullBleed && !isWide;
  // FormantKit unified shell: `.fk-shell` pins the viewport (100vh,
  // overflow hidden), `.fk-topbar` + `.fk-navrail` stay fixed, and
  // per-route overflow happens *inside* `<main>` — constrained pages
  // scroll `<main>` itself; wide / full-bleed routes own a deeper
  // scroll container (kanban column body, editor canvas, etc.).
  return (
    <ChatHostProvider>
      <div className="fk-shell">
        <header className="fk-topbar">
          <div className="fk-topbar__brand">
            <button
              type="button"
              className="fk-topbar__util"
              aria-label={railCollapsedEffective ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-pressed={railCollapsedEffective}
              onClick={toggleRail}
            >
              <svg
                className="icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to={routes.CHAT} aria-label="Formant — home" className="fk-topbar__logo">
              {/* Formant "F" mark — inlined from FormantKit assets/formant-logo-white.svg
                so it tints with currentColor + scales without a network round-trip. */}
              <svg
                viewBox="0 0 120 120"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
              >
                <rect x="40" y="14" width="71" height="21" />
                <polygon points="9,108 72,108 40,43" />
                <circle cx="78" cy="61" r="16" />
              </svg>
            </Link>
          </div>
          <div className="fk-topbar__context" ref={setIdentityNode} />
          <div className="fk-topbar__actions" ref={setActionsNode} />
          <div className="fk-topbar__utils">
            <HitlBell />
            {isFde && !isChatSurface(pathname) ? <ChatLauncher /> : null}
            {isFde ? <AppSwitcher /> : null}
          </div>
        </header>
        <div className="fk-shell__body">
          <ConsoleNavRail collapsed={railCollapsedEffective} />
          <main className="fk-shell__main" style={mainStyle}>
            <PageIdentityContext.Provider value={identityNode}>
              <PageActionsContext.Provider value={actionsNode}>
                {isConstrained ? (
                  <div style={constrainedInner}>
                    <Outlet />
                  </div>
                ) : (
                  <Outlet />
                )}
              </PageActionsContext.Provider>
            </PageIdentityContext.Provider>
          </main>
        </div>
      </div>
    </ChatHostProvider>
  );
}
